import { useState, useRef } from "react";
import Axios from "../../lib/axios/globalInterceptorSetup";
import type { IResponse } from "../../types/response.types";
import axios from "axios";

export type RequestMethod = "get" | "post" | "put" | "delete";

interface IUserHttpBase {
  path: string;
  pathParams?: Record<string, string | number>;
}

interface IUseHttp extends IUserHttpBase {
  method: RequestMethod;
  body?: any;
}

interface IUseGetHttp extends IUserHttpBase {}

interface IUsePostHttp<TBody> extends IUserHttpBase {
  body?: TBody;
}

export interface IUserHttpResponse<TBody, TResponse> {
  fetching: boolean;
  execute: (
    data: IUseHttp extends { method: "get" }
      ? IExecuteWithoutBody
      : IExecuteWithBody<TBody>
  ) => Promise<IResponse<TResponse>>;
  cancel: () => void; // Add cancel function
}

export interface IExecuteWithoutBody {
  queryParams?: Record<string, string | number | boolean>;
  pathParams?: Record<string, string | number>;
}

export interface IExecuteWithBody<TBody> extends IExecuteWithoutBody {
  body: TBody;
}

function useHttp<TBody, TResponse>(
  params: IUseHttp
): IUserHttpResponse<TBody, TResponse> {
  const [fetching, setFetching] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null); // Store AbortController

  // Cancel function to abort the request
  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  function replacePathParams(
    path: string,
    pathParams?: Record<string, string | number>
  ) {
    if (!pathParams) return path;

    let updatedPath = path;
    // biome-ignore lint/complexity/noForEach: <explanation>
    Object.keys(pathParams).forEach((key) => {
      const value = pathParams[key];
      updatedPath = updatedPath.replace(`:${key}`, String(value));
    });

    return updatedPath;
  }

  async function execute(
    data: IUseHttp extends { method: "get" }
      ? IExecuteWithoutBody
      : IExecuteWithBody<TBody>
  ): Promise<IResponse<TResponse>> {
    try {
      setFetching(true);
      abortControllerRef.current = new AbortController(); // Create a new controller

      // Get the final URL by replacing path params
      const url = replacePathParams(params.path, data?.pathParams);

      const response = await Axios({
        method: params.method,
        url,
        params: data?.queryParams,
        data: data?.body,
        signal: abortControllerRef.current.signal, // Pass the abort signal to Axios
      });

      const responseData = response.data as IResponse<TResponse>;
      return responseData;
    } catch (error) {
      if (axios.isCancel(error)) {
        return {
          requestId: "",
          ignoreError: true,
          success: false,
          error: {
            logout: false,
            message: "Request was canceled",
          },
        };
      }
      console.error(error);

      return {
        requestId: "",
        success: false,
        ignoreError: false,
        error: {
          logout: false,
          message:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
        },
      };
    } finally {
      setFetching(false);
    }
  }

  return {
    fetching,
    execute,
    cancel, // Return the cancel function
  };
}

export function useGetHttp<TResponse>(params: IUseGetHttp) {
  return useHttp<null, TResponse>({
    method: "get",
    path: params.path,
  });
}

export function usePostHttp<TBody, TResponse>(params: IUsePostHttp<TBody>) {
  return useHttp<TBody, TResponse>({
    method: "post",
    path: params.path,
  });
}

// Separate POST request for FormData
export function usePostFormDataHttp<TResponse>(params: IUsePostHttp<FormData>) {
  return useHttp<FormData, TResponse>({
    method: "post",
    path: params.path,
  });
}

export function usePutHttp<TBody, TResponse>(params: IUsePostHttp<TBody>) {
  return useHttp<TBody, TResponse>({
    method: "put",
    path: params.path,
  });
}

export function useDeleteHttp<TBody, TResponse>(params: IUsePostHttp<TBody>){
  return useHttp<TBody, TResponse>({
    method: "delete",
    path: params.path,
  });
}


